import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import CachedIcon from "@mui/icons-material/Cached";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";

import { setError } from "../../reducers/appSlice";
import BuscadorOrganismosMulti from "../../commons/BuscadorOrganismosMulti";
import ComboAccesoSistema from "../../commons/ComboAccesoSistema";

const Row = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

function mergeArrays(array1, array2) {
  let mergedArray = [...array1, ...array2];
  let uniqueArray = [];

  let ids = new Set();
  for (let i = 0; i < mergedArray.length; i++) {
    if (!ids.has(mergedArray[i].id)) {
      ids.add(mergedArray[i].id);
      uniqueArray.push(mergedArray[i]);
    }
  }

  return uniqueArray;
}

function atLeastOneOf(item) {
  return (
    this.parent.codigo ||
    this.parent.descripcion ||
    this.parent.accesoSistema ||
    this.parent.organismos
  );
}

const validationSchema = Yup.object().shape({
  codigo: Yup.number()
    .nullable()
    .test("oneOfRequired", "Al menos un campo es requerido", atLeastOneOf),
  descripcion: Yup.string()
    .nullable()
    .test("oneOfRequired", "Al menos un campo es requerido", atLeastOneOf),
  accesoSistema: Yup.string()
    .nullable()
    .test("oneOfRequired", "Al menos un campo es requerido", atLeastOneOf),
  organismos: Yup.string()
    .nullable()
    .test(
      "oneOfRequired",
      "Al menos un campo u organismo es requerido",
      atLeastOneOf
    ),
});

const Filtros = ({ buscar }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [searching, setSearching] = useState(false);
  const [organismos, setOrganismos] = useState([]);

  const handleChangeNumber = (event) => {
    const filter = event.target.value.replace(/\D/g, "");
    formik.setFieldValue(event.target.name, filter);
  };

  const handleOrganismos = (nuevos) =>
    setOrganismos(mergeArrays(organismos, nuevos));

  const generarLabelOrganismo = (organismo) => {
    const nombre = organismo.descOrgCompleta.split(" - ")[0];
    return `${organismo.id} - ${nombre}`;
  };

  const quitarOrganismo = (organismo) => {
    const nuevo = organismos.filter((elemento) => elemento.id !== organismo.id);
    console.log("Nuevo array:", nuevo);
    setOrganismos(nuevo);
  };

  const handleReset = () => {
    setOrganismos([]);
    formik.handleReset();
  };

  const formik = useFormik({
    initialValues: {
      codigo: "",
      descripcion: "",
      accesoSistema: "Todos",
      organismos: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setSearching(true);
        const result = await buscar(values);
        if (result.length > 0) setExpanded(false);
      } catch (error) {
        dispatch(setError(error.message));
      } finally {
        setSearching(false);
      }
    },
    onReset: () => {},
  });

  useEffect(() => {
    if (organismos && organismos.length > 0) {
      // Generar el string de ids concatenados por ";"
      const ids = organismos.map((organismo) => organismo.id).join(";");

      // Setear el valor en Formik
      formik.setFieldValue("organismos", ids);
    } else {
        formik.setFieldValue("organismos", "");
    }
  }, [organismos]);

  return (
    <Paper sx={{ px: 1, py: 1, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton>
            <FilterAltOutlinedIcon color="primary" />
          </IconButton>
          <Typography
            variant="cuerpo"
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: "block",
              cursor: "pointer",
            }}
          >
            {"Criterios de búsqueda"}
          </Typography>
        </Box>
        <ExpandMore
          expand={expanded}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label="show more"
          size="small"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </Box>

      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{ px: 1, py: 1 }}
      >
        <form
          id="filtrosBuscadorGrupos"
          name="filtrosBuscadorGrupos"
          onSubmit={formik.handleSubmit}
          onReset={handleReset}
          noValidate
          autoComplete="off"
        >
          <Row container spacing={1}>
            <Grid item lg={4} xs={12}>
              <TextField
                id="codigo"
                name="codigo"
                label="Código de grupo"
                variant="outlined"
                size="small"
                fullWidth
                onChange={handleChangeNumber}
                value={formik.values.codigo}
                error={formik.touched.codigo && Boolean(formik.errors.codigo)}
                helperText={formik.touched.codigo && formik.errors.codigo}
              />
            </Grid>
            <Grid item lg={4} xs={12}>
              <TextField
                id="descripcion"
                name="descripcion"
                label="Descripción del grupo"
                variant="outlined"
                size="small"
                fullWidth
                value={formik.values.descripcion.toUpperCase()}
                onChange={formik.handleChange}
                error={
                  formik.touched.descripcion &&
                  Boolean(formik.errors.descripcion)
                }
                helperText={
                  formik.touched.descripcion && formik.errors.descripcion
                }
              />
            </Grid>
            <Grid item lg={4} xs={12}>
              <ComboAccesoSistema
                name="accesoSistema"
                error={
                  formik.touched.accesoSistema &&
                  Boolean(formik.errors.accesoSistema)
                }
                helperText={
                  formik.touched.accesoSistema && formik.errors.accesoSistema
                }
                onChange={formik.handleChange}
                value={formik.values.accesoSistema}
                fullWidth
              />
            </Grid>
          </Row>
          <Stack direction="row" alignItems="center">
            <Typography variant="cuerpo">
              {"ORGANISMOS "}
              {/* {organismos.length === 0 &&
                                    <Typography variant="aclaracion">
                                        {"No hay organismos seleccionados"}
                                    </Typography>
                                } */}
            </Typography>
            <BuscadorOrganismosMulti
              resultado={handleOrganismos}
              activo={"S"}
              alta={"S"}
              accion="alta"
            />
            <IconButton title="Quitar todos" onClick={() => setOrganismos([])}>
              <CachedOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Grid container spacing={1} sx={{ py: 1 }}>
            {organismos.length === 0 && (
              <Grid item>
                <Typography variant="aclaracion" sx={{ ml: 1 }}>
                  {"No hay organismos seleccionados"}
                </Typography>
              </Grid>
            )}
            {organismos.map((organismo, index) => {
              return (
                <Grid key={organismo.id} item>
                  <Chip
                    color="primary"
                    variant="outlined"
                    label={generarLabelOrganismo(organismo)}
                    onDelete={() => quitarOrganismo(organismo)}
                  />
                </Grid>
              );
            })}
            <input type="hidden" value={formik.values.organismos} />
            {/* {formik.errors.organismos || ""} */}
            {/* {formik.values.organismos || ""} */}

            {/* 4110 4111 */}
          </Grid>
          <Box sx={{ display: "flex", flexDirection: "row-reverse" }}>
            <Button
              sx={{ m: 1 }}
              variant="gris"
              color="primary"
              endIcon={<SearchIcon />}
              type="submit"
              disabled={searching}
            >
              {searching ? "Buscando..." : "Buscar"}
            </Button>
            <Button
              variant="plata"
              type="reset"
              endIcon={<CachedIcon />}
              sx={{ m: 1 }}
            >
              Limpiar
            </Button>
          </Box>
        </form>
      </Collapse>
    </Paper>
  );
};

export default Filtros;
